/**
 * @description Component-TradeFormFooter
 */
import './index.less';

import { Side } from '@synfutures/sdks-perp';
import { useDebounceFn } from 'ahooks';
import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@/components/Alert';
import { useMediaQueryDevice } from '@/components/MediaQuery';
import WrappedButton from '@/components/WrappedButton';
import { FETCHING_STATUS } from '@/constants';
import { TRADE_TYPE } from '@/constants/trade';
import { useCurrentPairByDevice } from '@/features/pair/hook';
import {
  useCanPlaceCrossMarketOrder,
  useLimitFormStatus,
  useMarketFormStateStatus,
  useScaleFormStateStatus,
  useTradeSide,
} from '@/features/trade/hooks';
import { useSimulateBatchPlaceScaledLimitOrder } from '@/features/trade/query';
import { useSDK } from '@/features/web3/hook';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import { useWalletSigner } from '@/hooks/web3/useWalletNetwork';
import { formatDisplayNumber } from '@/utils/numberUtil';
import { useTradeFooterFormState } from './useTradeFooterStatus';

interface IPropTypes {
  disableBtn?: boolean;
  onTradeSuccess?: () => void;
  tradeType: TRADE_TYPE;
}

const TradeFormFooter: FC<IPropTypes> = function ({ disableBtn, tradeType }) {
  const { deviceType } = useMediaQueryDevice();
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const marketFormStatus = useMarketFormStateStatus(chainId);
  const limitFormStatus = useLimitFormStatus(chainId);
  const scaleLimitFormStateStatus = useScaleFormStateStatus(chainId);
  const tradeSide = useTradeSide(chainId);
  const { t } = useTranslation();
  const currentPair = useCurrentPairByDevice(chainId);
  const canPlaceCrossMarketOrder = useCanPlaceCrossMarketOrder(chainId);
  const sdk = useSDK(chainId);

  const fetchingStatus = useMemo(() => {
    switch (tradeType) {
      case TRADE_TYPE.LIMIT:
        return limitFormStatus;
      case TRADE_TYPE.SCALE_LIMIT:
        return scaleLimitFormStateStatus;
      default:
        return marketFormStatus;
    }
  }, [tradeType, marketFormStatus, limitFormStatus, scaleLimitFormStateStatus]);

  const { data: scaleLimitSimulation } = useSimulateBatchPlaceScaledLimitOrder(chainId);

  const {
    marginToDeposit,
    isDisableBtn,
    tradePrice,
    onTradeLimit,
    onTradeMarket,
    onTradeScaledLimit,
    onTradeCrossMarket,
  } = useTradeFooterFormState(tradeType, currentPair);

  const tradeText = useMemo(() => {
    return tradeSide === Side.LONG ? t('common.buyUpper') : t('common.sellUpper');
  }, [tradeSide, t]);
  const signer = useWalletSigner();

  const { run: onClickTradeButton } = useDebounceFn(
    async () => {
      if (chainId && userAddr && signer && currentPair) {
        try {
          if (tradeType === TRADE_TYPE.SCALE_LIMIT) {
            await onTradeScaledLimit();
          }
          if (tradeType === TRADE_TYPE.MARKET) {
            await onTradeMarket();
          } else if (tradeType === TRADE_TYPE.LIMIT) {
            canPlaceCrossMarketOrder ? await onTradeCrossMarket() : await onTradeLimit();
          }
        } catch (e) {}
      }
    },
    { wait: 500 },
  );

  return (
    <div className={`trade-form__footer ${deviceType}`}>
      {tradeType === TRADE_TYPE.SCALE_LIMIT && scaleLimitSimulation?.data && !scaleLimitSimulation?.message && (
        <Alert type="warning" showIcon={true} message={t('common.tradePage.warningMsg.scaleLimitNotice')} />
      )}
      <WrappedButton
        className={`trade-form__btn ${tradeSide === Side.SHORT ? 'trade-form__btn-short' : ''}`}
        loading={fetchingStatus === FETCHING_STATUS.FETCHING}
        disabled={disableBtn || isDisableBtn}
        amount={marginToDeposit?.wadValue}
        marginToken={currentPair?.rootInstrument.marginToken}
        spenderAddress={sdk?.perp?.contracts?.gate?.address}
        afterApproved={() => onClickTradeButton()}>
        {tradeText}
        <> &nbsp; {tradePrice ? '@ ' + formatDisplayNumber({ num: tradePrice || '', type: 'price' }) : ''} </>
      </WrappedButton>
    </div>
  );
};

export default memo(TradeFormFooter);
